import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MenuRow {
  readonly menuId: string;
  readonly menuCode: string;
  readonly menuName: string;
  readonly parentMenuCode: string | null;
  readonly menuSequence: number;
  readonly webVisible: boolean;
  readonly mobileVisible: boolean;
  readonly icon: string | null;
  readonly url: string | null;
}

/**
 * M22 L22.04 — Mobile Screen Registry (the O13 retro-host inventory).
 * Reconciled to the canonical admin-controlled menu registry
 * (t018_menu_master) rather than a new app_screen_registry table — every
 * screen (web + mobile) is indexed here with its host route and the surfaces
 * (web / mobile) it renders on. This is the single registry the mobile shell
 * and admin both read, honoring "ADMIN DECIDES PAGE ACCESS" and no-duplicate-tables.
 */
export function M22ScreenRegistryPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly MenuRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: readonly MenuRow[] } | readonly MenuRow[]>('/v1/menus', { token })
      .then((r) => {
        if (!alive) return;
        const list = Array.isArray(r) ? r : (r as { items: readonly MenuRow[] }).items;
        setRows(list);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const mobileCount = useMemo(
    () => (rows ? rows.filter((r) => r.mobileVisible).length : 0),
    [rows],
  );

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m22.screenRegistry.loading')}</p>;
  return (
    <main aria-label="mobile-screen-registry">
      <h1>{t('m22.screenRegistry.title')}</h1>
      <p aria-label="registry-summary">
        {t('m22.screenRegistry.summary', { total: rows.length, mobile: mobileCount })}
      </p>
      {rows.length === 0 ? (
        <p>{t('m22.screenRegistry.empty')}</p>
      ) : (
        <table aria-label="mobile-screen-registry-table">
          <thead>
            <tr><th>{t('m22.screenRegistry.colCode')}</th><th>{t('m22.screenRegistry.colName')}</th><th>{t('m22.screenRegistry.colRoute')}</th><th>{t('m22.screenRegistry.colWeb')}</th><th>{t('m22.screenRegistry.colMobile')}</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.menuId}>
                <td>{r.menuCode}</td>
                <td>{r.menuName}</td>
                <td>{r.url ?? '—'}</td>
                <td>{r.webVisible ? t('m22.screenRegistry.yes') : t('m22.screenRegistry.no')}</td>
                <td>{r.mobileVisible ? t('m22.screenRegistry.yes') : t('m22.screenRegistry.no')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
